/**
 * Editorial relative date formatting per UX §7.1.
 * No heavy date library — Intl.DateTimeFormat only.
 *
 *   < 1 day:           "Today" / "Yesterday"
 *   2-6 days:          "Last Tuesday"
 *   Same year, >7d:    "March 14"
 *   Prior years:       "March 14, 2024"
 */

const MS_IN_DAY = 24 * 60 * 60 * 1000;

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function daysBetween(later: Date, earlier: Date): number {
  return Math.round(
    (startOfDay(later).getTime() - startOfDay(earlier).getTime()) / MS_IN_DAY,
  );
}

function locale(): string {
  if (typeof navigator !== 'undefined' && navigator.language) {
    return navigator.language;
  }
  return 'en-US';
}

/**
 * Editorial relative — "Today", "Yesterday", "Last Tuesday", "March 14",
 * or "March 14, 2024". Always returns capitalized, English-style output.
 */
export function formatRelative(
  iso: string | Date,
  now: Date = new Date(),
): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';

  const days = daysBetween(now, d);

  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days >= 2 && days <= 6) {
    const weekday = new Intl.DateTimeFormat(locale(), {
      weekday: 'long',
    }).format(d);
    return `Last ${weekday}`;
  }
  if (d.getFullYear() === now.getFullYear()) {
    return new Intl.DateTimeFormat(locale(), {
      month: 'long',
      day: 'numeric',
    }).format(d);
  }
  return new Intl.DateTimeFormat(locale(), {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Full timeline date — always month + day + year, tabular figures.
 */
export function formatTimelineDate(iso: string | Date): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale(), {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(d);
}

/**
 * Sparkline-tooltip short date — "Mar 14".
 */
export function formatShortMonthDay(iso: string | Date): string {
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(locale(), {
    month: 'short',
    day: 'numeric',
  }).format(d);
}
